'use client';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/core/ui/primitives/accordion';
import { Badge } from '@/core/ui/primitives/badge';
import { Separator } from '@/core/ui/primitives/separator';
import { Switch } from '@/core/ui/primitives/switch';
import { cn } from '@core/helpers/misc/GeneralUtils';

interface Privilege {
  id: string;
  scope: string;
  feature: string;
  description: string;
}

interface PrivilegeAccordionProps {
  groupedPrivileges: Record<string, Privilege[]>;
  enabledFeatures: Record<string, boolean>;
  selected: Set<string>;
  onToggle: (privilegeId: string, next: boolean) => void;
  defaultOpenAll?: boolean;
}
export function PrivilegeAccordion({
  groupedPrivileges,
  enabledFeatures,
  selected,
  onToggle,
  defaultOpenAll = false,
}: PrivilegeAccordionProps) {
  const allKeys = Object.keys(groupedPrivileges);
  return (
    <Accordion
      type='multiple'
      defaultValue={defaultOpenAll ? allKeys : []}
      className='overflow-hidden rounded-xl border border-border bg-muted/15 divide-y divide-border'>
      {Object.entries(groupedPrivileges).map(([feature, featurePrivileges]) => {
        const isEnabled = enabledFeatures[feature];
        let selectedCount = 0;
        for (const privilege of featurePrivileges) {
          if (selected.has(privilege.id)) selectedCount += 1;
        }
        return (
          <AccordionItem key={feature} value={feature} className='border-none'>
            <AccordionTrigger className='px-4 py-3 bg-muted/50 hover:bg-muted/60'>
              <div className='flex w-full items-center justify-between gap-2'>
                <div className='flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground'>
                  {feature}
                  <Badge variant='secondary' className='h-5 px-2 text-[10px] font-medium'>
                    {selectedCount}/{featurePrivileges.length}
                  </Badge>
                  {!isEnabled && (
                    <Badge variant='outline' className='h-5 px-2 text-[10px] font-medium'>
                      Disabled
                    </Badge>
                  )}
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className='divide-y divide-border border-t border-border px-0 py-0'>
              {featurePrivileges.map((privilege) => {
                const isChecked = selected.has(privilege.id);
                return (
                  <div
                    key={privilege.id}
                    className={cn(
                      'flex items-center justify-between gap-4 px-4 py-2',
                      'transition-colors hover:bg-muted/40 focus-within:bg-muted/40',
                      !isEnabled && 'opacity-60 grayscale'
                    )}>
                    <div className='grid flex-1 grid-cols-[minmax(150px,200px)_1px_1fr] items-center gap-3 text-sm'>
                      <span className='font-medium truncate font-mono text-[11px]' title={privilege.id}>
                        {privilege.id}
                      </span>
                      <Separator orientation='vertical' className='self-stretch w-px bg-border justify-self-center' />
                      <span className='text-xs text-muted-foreground leading-relaxed line-clamp-2'>
                        {privilege.description}
                      </span>
                    </div>
                    <Switch
                      checked={isChecked}
                      onCheckedChange={(next) => onToggle(privilege.id, next)}
                      disabled={!isEnabled}
                      aria-label={`Toggle privilege ${privilege.id}`}
                    />
                  </div>
                );
              })}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
